'use client'

import type { Coordinates, Location } from 'maps/types'
import { ENDPOINT_GEOCODING_SERVICE } from 'maps/utils/constants'
import { fetcher } from 'services/rest/fetcher'
import { env } from 'utils/envManager'

type Geometry = {
	locationType: string
	location: Coordinates
}

type WoosmapGeocode = {
	public_id?: string
	types?: string[]
	formatted_address?: string
	geometry?: Geometry
	address_components?: AddressComponent[]
}

enum AddressComponentType {
	postalCode = 'postal_code',
	cityName = 'locality',
	route = 'route',
}

type AddressComponent = {
	short_name: string
	long_name: string
	types: string[]
}

type WoosmapGeocodeResponse = {
	results: WoosmapGeocode[]
	status: string
}

interface GeocodeProps {
	address?: string
	coordinates?: Coordinates
	languageISO: string
	countryISO: string
}

const getParams = (props: GeocodeProps) => {
	const { address, coordinates, languageISO, countryISO } = props
	const params: Record<string, string> = {
		language: languageISO,
		components: `country:${countryISO}`,
		types: 'city|locality|postal_code|address',
	}

	if (address) {
		params.address = `${address}, ${countryISO}`.toLowerCase()
	}

	if (!(address || coordinates)) {
		params.address = countryISO.toLowerCase()
	}

	if (coordinates) {
		const latlng = `${coordinates.lat},${coordinates.lng}`
		params.latlng = latlng
	}

	return params
}

/** Get the coordinates from an address (geocode) or an address from coordinates (reverse) */
export async function geocode(props: GeocodeProps): Promise<Location | null> {
	const { address } = props
	try {
		const url = new URL(
			ENDPOINT_GEOCODING_SERVICE,
			env.NEXT_PUBLIC_SITE_BASE_URL
		)
		const params = getParams(props)

		url.search = new URLSearchParams(params).toString()

		const data = await fetcher<WoosmapGeocodeResponse>(url.href)

		const { geometry, address_components } = data?.results[0] ?? {}
		const { lat, lng } = geometry?.location ?? {}

		if (!(lat && lng)) {
			return null
		}
		const postalCode = address_components?.find((component) =>
			component.types.includes(AddressComponentType.postalCode)
		)?.long_name
		const cityName = address_components?.find((component) =>
			component.types.includes(AddressComponentType.cityName)
		)?.long_name
		const route = address_components?.find((component) =>
			component.types.includes(AddressComponentType.route)
		)?.long_name

		// In the case of a reverse request, the address must be created from the response
		const fullAddress =
			address ??
			`${route ?? ''} ${postalCode ?? ''} ${cityName ?? ''}`
				.replace(/\s+/g, ' ')
				.trim()

		return {
			coordinates: { lat, lng },
			addressInfo: { address: fullAddress, postalCode, cityName },
		}
	} catch {
		console.error(`[Maps] Error geocoding address [${address}]`)
		return null
	}
}
